import type { ComplianceReport } from '@cls/shared';

export function exportPdf(reportEl: HTMLElement | null, title: string): void {
  if (!reportEl || !reportEl.innerHTML.trim()) return;
  const html = `<!DOCTYPE html><html><head><title>${escapeHtml(title)}</title><style>
    body{font-family:Arial,sans-serif;font-size:10px;padding:16px;color:#1a2035}
    h1{font-size:16px;margin-bottom:2px}p{color:#666;font-size:10px;margin-bottom:10px}
    table{width:100%;border-collapse:collapse}
    th{background:#1a2035;color:#E8A427;padding:6px 8px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:1px}
    td{padding:5px 8px;border-bottom:1px solid #eee;font-size:10px}
    @media print{body{padding:0}}
  </style></head><body>${reportEl.innerHTML}</body></html>`;
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 500);
}

export function exportExcel(report: ComplianceReport): void {
  const rows: Array<Array<string | number>> = [];
  rows.push(['CLS Compliance Report', `Generated: ${new Date().toLocaleString('en-GB')}`]);
  rows.push([]);

  rows.push(['EXPIRED CERTIFICATES']);
  rows.push(['Worker', 'Role', 'Certificate', 'Expired']);
  for (const r of report.expiredCerts) rows.push([r.workerName, r.role, r.certType, r.expiry]);
  rows.push([]);

  rows.push(['EXPIRING WITHIN 30 DAYS']);
  rows.push(['Worker', 'Certificate', 'Expires', 'Days Left']);
  for (const r of report.expiring30) rows.push([r.workerName, r.certType, r.expiry, r.daysLeft]);
  rows.push([]);

  rows.push(['MISSING MANDATORY CERTIFICATES']);
  rows.push(['Worker', 'Role', 'Missing Certificate']);
  for (const r of report.missingMandatory) rows.push([r.workerName, r.roles.join(', '), r.certType]);
  rows.push([]);

  rows.push(['NON-COMPLIANT SUPPLIERS']);
  rows.push(['Supplier', 'Category', 'Status']);
  for (const s of report.nonCompliantSuppliers) rows.push([s.name, s.category, 'Non-Compliant']);
  rows.push([]);

  rows.push(['UNSIGNED DOCUMENTS']);
  rows.push(['Document', 'Project', 'Awaiting Signature From']);
  for (const d of report.unsignedDocs) rows.push([d.docName, d.projectLabel, d.pendingNames.join(', ')]);
  rows.push([]);

  rows.push(['GEOFENCE ANOMALIES']);
  rows.push(['Worker', 'Project', 'Type', 'Date/Time', 'Distance (m)']);
  for (const g of report.geofenceAnomalies) {
    rows.push([
      g.workerName,
      g.projectLabel,
      g.type.toUpperCase(),
      new Date(g.timestamp).toLocaleString('en-GB'),
      g.distanceM != null ? Math.round(g.distanceM) : '',
    ]);
  }
  rows.push([]);

  rows.push(['DATA QUALITY']);
  rows.push(['Entity', 'Type', 'Field', 'Severity']);
  for (const i of report.dataAudit.issues) {
    rows.push([i.entity, i.entityType, i.field, i.severity]);
  }

  const csv = rows
    .map((row) => row.map((cell) => csvEscape(cell)).join(','))
    .join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `CLS_Compliance_Report_${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportEmail(title: string): void {
  const subject = `${title} - ${new Date().toLocaleDateString('en-GB')}`;
  const body =
    'Attached is the latest compliance report from CLS Construction Logistics Services.\n\nUse the PDF or Excel export buttons in the report to attach the data file before sending.';
  window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
}

function csvEscape(value: string | number): string {
  const s = String(value ?? '');
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
